// A Twetch smart tag for ANY Ordinals Wallet market — an ordinals collection,
// a rune, a BRC-20, a TAP token. One namespace, keyed by the market's OW slug,
// so a thesis about it and the feed that lists those theses agree on identity.
//
// Twetch ids are ASCII (`A-Za-z0-9 : / . _ -`, max 128), but OW slugs are not:
// runes carry `•`, TAP tickers carry spaces and `#`, BRC-20 tickers can be any
// four bytes. Safe slugs ride readable as `slug/<slug>`; anything else is
// base64url'd as `b64/<…>`. The prefix makes the two forms unambiguous and
// the mapping reversible.

import type { TwetchSmartTag } from './twetch-api'

export const OW_NAMESPACE = 'ordinalswallet'
const SAFE_SLUG = /^[A-Za-z0-9._:-]+$/
const MAX_ID = 128

function base64Url(bytes: Uint8Array): string {
	let binary = ''
	for (const byte of bytes) binary += String.fromCharCode(byte)
	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/// Null when the slug cannot be expressed within Twetch's id limit.
export function collectionTagId(slug: string): string | null {
	const trimmed = slug.trim()
	if (!trimmed) return null
	const id = SAFE_SLUG.test(trimmed)
		? `slug/${trimmed}`
		: `b64/${base64Url(new TextEncoder().encode(trimmed))}`
	return id.length <= MAX_ID ? id : null
}

export function slugFromTagId(id: string): string | null {
	if (id.startsWith('slug/')) return id.slice(5) || null
	if (!id.startsWith('b64/')) return null
	try {
		const padded = id.slice(4).replace(/-/g, '+').replace(/_/g, '/')
		const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4))
		return new TextDecoder().decode(Uint8Array.from(binary, (c) => c.charCodeAt(0))) || null
	} catch {
		return null
	}
}

/// The cashtag a thesis wears. Twetch's grammar: 1–15 ASCII letters/digits,
/// starting with a letter. Derived from the SLUG, never the display name: the
/// slug is the market's stable identity, while names are edited, localized and
/// sometimes missing. It is display text — identity is the tag id — so two
/// markets sharing a symbol is harmless.
export function collectionSymbol(slug: string): string {
	const ticker = /^(?:rune|brc20|tap|brc420)-(.+)$/i.exec(slug)?.[1]
	const letters = (ticker ?? slug)
		.normalize('NFKD')
		.replace(/[^A-Za-z0-9]/g, '')
		.toUpperCase()
	const symbol = /^[A-Z]/.test(letters) ? letters : `OW${letters}`
	return symbol.slice(0, 15)
}

export function collectionSmartTag(slug: string): TwetchSmartTag | null {
	const id = collectionTagId(slug)
	return id ? { namespace: OW_NAMESPACE, id, symbol: collectionSymbol(slug) } : null
}

/// Collection slugs historically moved between underscore and hyphen
/// separators (for example `taproot_wizards` -> `taproot-wizards`). Posts keep
/// the exact smart-tag identity they were written with, so collection reads
/// must include those separator-equivalent legacy identities or the posts look
/// deleted after a slug cleanup.
export function collectionSmartTagAliases(slug: string): TwetchSmartTag[] {
	const trimmed = slug.trim()
	if (!trimmed) return []
	const slugs = [trimmed, trimmed.replace(/-/g, '_'), trimmed.replace(/_/g, '-')]
	const seen = new Set<string>()
	const tags: TwetchSmartTag[] = []
	for (const candidate of slugs) {
		const tag = collectionSmartTag(candidate)
		if (tag && !seen.has(tag.id)) {
			seen.add(tag.id)
			tags.push(tag)
		}
	}
	return tags
}
